import React, { useState } from 'react';
import { Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { FolderSelect } from 'components/forms/FolderSelect';
import ScheduleWindowsSelect from './Schedules';
import type { Worker } from '../../../../redux/slices/connectionsSlice';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

export interface CreateQueueFormState {
  queue_name: string;
  queue_limit: number;
  queue_status: string;
  zone_name: string;
  queue_description: string;
  workers: { worker_id: number; worker_name: string; priority: number }[];
  connections: { connection_id: number; connection_name: string }[];
  scheduling_window_id: number | null;
  queue_allocation_statuses: string[];
  folder_id: number | null;
}

type Props = {
  show: boolean;
  value: CreateQueueFormState;

  zones: { id: string; name: string }[];
  connections: any[];
  workers: Worker[];

  allocationStatusOptions: string[];

  onChange: (next: CreateQueueFormState) => void;
  onClose: () => void;
  onSave: (form: CreateQueueFormState) => void | Promise<void>;
};

const CreateQueueModal: React.FC<Props> = ({ show, value, zones, connections, workers, allocationStatusOptions, onChange, onClose, onSave }) => {
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const isDirty = useUnsavedChanges(show, value);

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const setField = (name: string, v: any) => onChange({ ...value, [name]: v });

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value: v } = e.target;

    const parsed = name === 'queue_limit' ? Number(v) || 0 : v;
    const next: CreateQueueFormState = { ...value, [name]: parsed };

    // If zone changes, reset schedule and folder to avoid cross-zone mismatch
    if (name === 'zone_name') {
      next.scheduling_window_id = null;
      next.folder_id = null;
    }

    onChange(next);
  };

  const handleSchedulingWindowChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    setField('scheduling_window_id', raw === '' ? null : parseInt(raw, 10));
  };

  const handleConnectionSelection = (connection: any, checked: boolean) => {
    const list = Array.isArray(value.connections) ? value.connections : [];
    const updated = checked
      ? [
          ...list,
          {
            connection_id: connection.connection_id,
            connection_name: connection.connection_name,
          },
        ]
      : list.filter((c: any) => c.connection_id !== connection.connection_id);

    setField('connections', updated);
  };

  const handleWorkerSelection = (worker: Worker, checked: boolean) => {
    const list = Array.isArray(value.workers) ? value.workers : [];
    const updated = checked
      ? [
          ...list,
          {
            worker_id: worker.worker_id,
            worker_name: worker.worker_name,
            priority: 0,
          },
        ]
      : list.filter((w: any) => w.worker_id !== worker.worker_id);

    setField('workers', updated);
  };

  const handleWorkerPriorityChange = (worker_id: number, priority: number) => {
    setField(
      'workers',
      (value.workers || []).map((w: any) => (w.worker_id === worker_id ? { ...w, priority } : w))
    );
  };

  const handleAllocationStatusSelection = (status: string, checked: boolean) => {
    const list = Array.isArray(value.queue_allocation_statuses) ? value.queue_allocation_statuses : [];
    const updated = checked ? Array.from(new Set([...list, status])) : list.filter((s: string) => s !== status);

    setField('queue_allocation_statuses', updated);
  };

  return (
    <Modal show={show} onHide={handleRequestClose}>
      <Modal.Header closeButton>
        <Modal.Title>Create New Queue</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Queue Name</Form.Label>
            <Form.Control type="text" name="queue_name" value={value.queue_name} onChange={handleChange} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Queue Status</Form.Label>
            <Form.Select name="queue_status" value={value.queue_status} onChange={handleChange}>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Zone</Form.Label>
            <Form.Select name="zone_name" value={value.zone_name} onChange={handleChange}>
              <option value="">Select a Zone</option>
              {zones.map((z) => (
                <option key={z.id} value={z.name}>
                  {z.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Folder</Form.Label>
            <FolderSelect value={value.folder_id} onChange={(folder_id) => setField('folder_id', folder_id)} zoneName={value.zone_name} />
          </Form.Group>

          <ScheduleWindowsSelect
            selectedValue={value.scheduling_window_id}
            queryParams={{ zone_name: value.zone_name || undefined }}
            onChange={handleSchedulingWindowChange}
            placeholder="-- (no schedule window)"
          />

          <Form.Group className="mb-3">
            <Form.Label>Queue Limit</Form.Label>
            <Form.Control type="number" name="queue_limit" value={value.queue_limit} onChange={handleChange} />
            <Form.Text className="text-muted">Enter 0 for no limit</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={3} name="queue_description" value={value.queue_description} onChange={handleChange} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Allocation Statuses{' '}
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="create-allocation-statuses-tooltip">
                    Determines which node instance statuses count toward the Queue Limit. Only instances in these statuses occupy a concurrency slot.
                    <br />
                    <br />
                    <strong>Default:</strong> running, started, dispatched, WorkflowWait, killing.
                    <br />
                    <br />
                    <strong>Example:</strong> if Queue Limit is 5 and only &quot;running&quot; is selected, up to 5 instances can run simultaneously while
                    dispatched or waiting instances don&apos;t count toward the limit.
                  </Tooltip>
                }
              >
                <span style={{ cursor: 'help', opacity: 0.6, fontSize: '1em' }} title="">
                  <FontAwesomeIcon icon={faCircleInfo} />
                </span>
              </OverlayTrigger>
            </Form.Label>
            <div className="border rounded p-2">
              {allocationStatusOptions.map((s) => {
                const checked = Array.isArray(value.queue_allocation_statuses) && value.queue_allocation_statuses.includes(s);
                return (
                  <Form.Check
                    key={s}
                    type="checkbox"
                    className="mb-1"
                    label={s}
                    checked={!!checked}
                    onChange={(e) => handleAllocationStatusSelection(s, e.target.checked)}
                  />
                );
              })}
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Connections</Form.Label>
            <div className="overflow-auto border rounded p-2" style={{ maxHeight: 260 }}>
              {[...connections]
                .sort((a, b) => (a.connection_name || '').localeCompare(b.connection_name || ''))
                .map((c: any) => {
                  const isChecked = Array.isArray(value.connections) && value.connections.some((x: any) => x.connection_id === c.connection_id);

                  return (
                    <Form.Check
                      key={c.connection_id}
                      type="checkbox"
                      className="mb-1"
                      label={`${c.connection_name}${c.connection_type ? ` (${c.connection_type})` : ''}`}
                      checked={!!isChecked}
                      onChange={(e) => handleConnectionSelection(c, e.target.checked)}
                    />
                  );
                })}
            </div>
          </Form.Group>

          <Form.Group controlId="workers">
            <Form.Label>Workers</Form.Label>
            {workers.map((w: Worker) => {
              const checked = Array.isArray(value.workers) ? value.workers.some((x: any) => x.worker_id === w.worker_id) : false;

              const current = Array.isArray(value.workers) ? value.workers.find((x: any) => x.worker_id === w.worker_id) : undefined;

              return (
                <div key={w.worker_id} className="mb-2">
                  <Form.Check type="checkbox" label={w.worker_name} checked={!!checked} onChange={(e) => handleWorkerSelection(w, e.target.checked)} />
                  {checked && (
                    <Form.Control
                      type="number"
                      className="mt-1"
                      value={current?.priority ?? 0}
                      onChange={(e) => handleWorkerPriorityChange(w.worker_id, parseInt(e.target.value || '0', 10))}
                      placeholder="Priority"
                    />
                  )}
                </div>
              );
            })}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleRequestClose}>
          Close
        </Button>
        <Button variant="primary" onClick={() => onSave(value)}>
          Save and Close
        </Button>
      </Modal.Footer>
      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </Modal>
  );
};

export default CreateQueueModal;
